import T from 'Components/Helpers/translate';
import AppService from 'App/legacy/AppService';
import { Feature, hasSoftDeployAccess } from 'App/services/FeatureHelperStandalone';
import type CustomField from 'App/resources/Model/CustomField';

/*
	The entity tag catalogue, extracted from the Angular TagsService (WEB-7834). Serves the
	{{Entity.Field}} tags used by document templates, triggers, the tag drawer and the import mapper.

	The catalogue is built lazily rather than at module load. Every entry is a translated string and
	most depend on metadata (standard fields, custom fields, currencies), so it cannot be built before
	those are in place — the Angular service built it inside AppService.loadedPromise for the same
	reason. buildTags() is memoised and runs on first read instead, which keeps the module free of
	load-order assumptions.

	Behaviour is preserved verbatim, quirks included — see the comments on getEntityTags and
	getImportTags. This is a decouple, not a cleanup.

	The mail-tag half of the Angular service (getMailTagList / flattenMailTagList / getTagsByEntity2)
	stays behind for now: it falls back to the Angular-only TagService.getMailTags whenever
	tagsByEntity has no entry for the requested entity, and both React callers take exactly that path.
*/

export type EntityTag = {
	name: string;
	tag: string;
	pdfTemplateTag?: string;
	description?: string;
	import?: boolean;
	type?: string;
	importMatch?: boolean;
	importDesc?: string;
	// Not part of the catalogue — stamped on by getEntityTags/getImportTags as tags are handed out.
	entity?: string;
	tagId?: number | string;
	nameMapper?: string;
	datatype?: string;
};

type EntityTagMap = { [entity: string]: EntityTag[] };
type TagsByEntityMap = { [entity: string]: string[] };
type CustomFieldKey = Parameters<typeof AppService.getCustomFields>[0];
type Currency = { iso: string; active: boolean; masterCurrency: boolean };
type PriceList = ReturnType<typeof AppService.getPriceLists>[number];

const t = T;

const nameMapper: { [entity: string]: string } = {
	account: 'Client',
	client: 'Client',
	general: 'General',
	contact: 'Contact',
	order: 'Order',
	activity: 'Activity',
	appointment: 'Appointment',
	project: 'Project',
	agreement: 'Agreement',
	clientrelation: '',
	product: 'Product',
	projectplan: 'ProjectPlan',
	ticket: 'Ticket'
};

const customFieldMapper: { [entity: string]: string } = {
	projectplan: 'projectPlan'
};

let cached: { tags: EntityTagMap; tagsByEntity: TagsByEntityMap } | null = null;

const buildTags = (): { tags: EntityTagMap; tagsByEntity: TagsByEntityMap } => {
	if (cached) {
		return cached;
	}

	const hasNewFields = hasSoftDeployAccess(Feature.NEW_FIELDS);

	const tags: EntityTagMap = {
		user: [],
		client: [
			{
				name: t('tag.account.id'),
				tag: 'Client.Id',
				pdfTemplateTag: 'client.id',
				description: t('tag.account.idDescription'),
				import: true,
				type: 'Integer',
				importMatch: true,
				importDesc: t('tag.import.client.id')
			},
			{
				name: t('tag.account.parentId'),
				tag: 'Client.ParentId',
				description: t('tag.account.parentIdDescription'),
				import: true,
				type: 'Integer',
				importDesc: t('tag.import.client.parentId')
			},
			{
				name: t('tag.account.name'),
				tag: 'Client.Name',
				pdfTemplateTag: 'client.name',
				description: t('tag.account.nameDescription'),
				import: true,
				type: 'String',
				importMatch: true,
				importDesc: t('tag.import.client.name')
			},

			{
				name: t('tag.client.address.delivery'),
				tag: 'Client.DeliveryAddress',
				pdfTemplateTag: 'client.tagAddresses.Delivery.address',
				description: t('tag.account.addressDescription'),
				import: true,
				type: 'String',
				importMatch: true,
				importDesc: t('tag.import.client.deliveryAddress')
			},
			{
				name: t('tag.client.zipcode.delivery'),
				tag: 'Client.DeliveryZipCode',
				pdfTemplateTag: 'client.tagAddresses.Delivery.zipcode',
				description: t('tag.account.zipcodeDescription'),
				import: true,
				type: 'String',
				importMatch: true,
				importDesc: t('tag.import.client.deliveryAddress.zip')
			},
			{
				name: t('tag.client.municipality.delivery'),
				tag: 'Client.DeliveryMunicipality',
				pdfTemplateTag: 'client.tagAddresses.Delivery.municipality',
				import: true,
				type: 'String',
				importMatch: true,
				importDesc: t('tag.import.client.deliveryAddress.municipality')
			},
			{
				name: t('tag.client.city.delivery'),
				tag: 'Client.DeliveryCity',
				pdfTemplateTag: 'client.tagAddresses.Delivery.city',
				description: t('tag.account.cityDescription'),
				import: true,
				type: 'String',
				importMatch: true,
				importDesc: t('tag.import.client.deliveryAddress.city')
			},
			{
				name: t('tag.client.state.delivery'),
				tag: 'Client.DeliveryState',
				pdfTemplateTag: 'client.tagAddresses.Delivery.state',
				description: t('tag.account.stateDescription'),
				import: true,
				type: 'String',
				importMatch: true,
				importDesc: t('tag.import.client.deliveryAddress.state')
			},
			{
				name: t('tag.client.country.delivery'),
				tag: 'Client.DeliveryCountry',
				pdfTemplateTag: 'client.tagAddresses.Delivery.country',
				description: t('tag.account.countryDescription'),
				import: true,
				type: 'String',
				importMatch: true,
				importDesc: t('tag.import.client.deliveryAddress.country')
			},

			{
				name: t('tag.client.address.visit'),
				tag: 'Client.VisitAddress',
				pdfTemplateTag: 'client.tagAddresses.Visit.address',
				description: t('tag.account.addressDescription'),
				import: true,
				type: 'String',
				importMatch: true,
				importDesc: t('tag.import.client.visitAddress')
			},
			{
				name: t('tag.client.zipcode.visit'),
				tag: 'Client.VisitZipCode',
				pdfTemplateTag: 'client.tagAddresses.Visit.zipcode',
				description: t('tag.account.zipcodeDescription'),
				import: true,
				type: 'String',
				importMatch: true,
				importDesc: t('tag.import.client.visitAddress.zip')
			},
			{
				name: t('tag.client.municipality.visit'),
				tag: 'Client.VisitMunicipality',
				pdfTemplateTag: 'client.tagAddresses.Visit.municipality',
				import: true,
				type: 'String',
				importMatch: true,
				importDesc: t('tag.import.client.visitAddress.municipality')
			},
			{
				name: t('tag.client.state.visit'),
				tag: 'Client.VisitState',
				pdfTemplateTag: 'client.tagAddresses.Visit.state',
				description: t('tag.account.stateDescription'),
				import: true,
				type: 'String',
				importMatch: true,
				importDesc: t('tag.import.client.visitAddress.state')
			},
			{
				name: t('tag.client.city.visit'),
				tag: 'Client.VisitCity',
				pdfTemplateTag: 'client.tagAddresses.Visit.city',
				description: t('tag.account.cityDescription'),
				import: true,
				type: 'String',
				importMatch: true,
				importDesc: t('tag.import.client.visitAddress.city')
			},
			{
				name: t('tag.client.country.visit'),
				tag: 'Client.VisitCountry',
				pdfTemplateTag: 'client.tagAddresses.Visit.country',
				description: t('tag.account.countryDescription'),
				import: true,
				type: 'String',
				importMatch: true,
				importDesc: t('tag.import.client.visitAddress.country')
			},

			{
				name: t('tag.client.address.invoice'),
				tag: 'Client.InvoiceAddress',
				pdfTemplateTag: 'client.tagAddresses.Invoice.address',
				description: t('tag.account.addressDescription'),
				import: true,
				type: 'String',
				importMatch: true,
				importDesc: t('tag.import.client.billingAddress')
			},
			{
				name: t('tag.client.zipcode.invoice'),
				tag: 'Client.InvoiceZipCode',
				pdfTemplateTag: 'client.tagAddresses.Invoice.zipcode',
				description: t('tag.account.zipcodeDescription'),
				import: true,
				type: 'String',
				importMatch: true,
				importDesc: t('tag.import.client.billingAddress.zip')
			},
			{
				name: t('tag.client.municipality.invoice'),
				tag: 'Client.InvoiceMunicipality',
				pdfTemplateTag: 'client.tagAddresses.Invoice.municipality',
				import: true,
				type: 'String',
				importMatch: true,
				importDesc: t('tag.import.client.billingAddress.municipality')
			},
			{
				name: t('tag.client.state.invoice'),
				tag: 'Client.InvoiceState',
				pdfTemplateTag: 'client.tagAddresses.Invoice.state',
				description: t('tag.account.stateDescription'),
				import: true,
				type: 'String',
				importMatch: true,
				importDesc: t('tag.import.client.billingAddress.state')
			},
			{
				name: t('tag.client.city.invoice'),
				tag: 'Client.InvoiceCity',
				pdfTemplateTag: 'client.tagAddresses.Invoice.city',
				description: t('tag.account.cityDescription'),
				import: true,
				type: 'String',
				importMatch: true,
				importDesc: t('tag.import.client.billingAddress.city')
			},
			{
				name: t('tag.client.country.invoice'),
				tag: 'Client.InvoiceCountry',
				pdfTemplateTag: 'client.tagAddresses.Invoice.country',
				description: t('tag.account.countryDescription'),
				import: true,
				type: 'String',
				importMatch: true,
				importDesc: t('tag.import.client.billingAddress.country')
			},

			{
				name: t('tag.client.address.post'),
				tag: 'Client.MailAddress',
				pdfTemplateTag: 'client.tagAddresses.Mail.address',
				description: t('tag.account.addressDescription'),
				import: true,
				type: 'String',
				importMatch: true,
				importDesc: t('tag.import.client.postalAddress')
			},
			{
				name: t('tag.client.zipcode.post'),
				tag: 'Client.MailZipCode',
				pdfTemplateTag: 'client.tagAddresses.Mail.zipcode',
				description: t('tag.account.zipcodeDescription'),
				import: true,
				type: 'String',
				importMatch: true,
				importDesc: t('tag.import.client.postalAddress.zip')
			},
			{
				name: t('tag.client.municipality.post'),
				tag: 'Client.MailMunicipality',
				pdfTemplateTag: 'client.tagAddresses.Mail.municipality',
				import: true,
				type: 'String',
				importMatch: true,
				importDesc: t('tag.import.client.postalAddress.municipality')
			},
			{
				name: t('tag.client.state.post'),
				tag: 'Client.MailState',
				pdfTemplateTag: 'client.tagAddresses.Mail.state',
				description: t('tag.account.stateDescription'),
				import: true,
				type: 'String',
				importMatch: true,
				importDesc: t('tag.import.client.postalAddress.state')
			},
			{
				name: t('tag.client.city.post'),
				tag: 'Client.MailCity',
				pdfTemplateTag: 'client.tagAddresses.Mail.city',
				description: t('tag.account.cityDescription'),
				import: true,
				type: 'String',
				importMatch: true,
				importDesc: t('tag.import.client.postalAddress.city')
			},
			{
				name: t('tag.client.country.post'),
				tag: 'Client.MailCountry',
				pdfTemplateTag: 'client.tagAddresses.Mail.country',
				description: t('tag.account.countryDescription'),
				import: true,
				type: 'String',
				importMatch: true,
				importDesc: t('tag.import.client.postalAddress.country')
			},

			{
				name: t('tag.client.address.other'),
				tag: 'Client.OtherAddress',
				pdfTemplateTag: 'client.tagAddresses.Other.address',
				description: t('tag.account.addressDescription'),
				import: true,
				type: 'String',
				importMatch: true,
				importDesc: t('tag.import.client.otherAddress')
			},
			{
				name: t('tag.client.zipcode.other'),
				tag: 'Client.OtherZipCode',
				pdfTemplateTag: 'client.tagAddresses.Other.zipcode',
				description: t('tag.account.zipcodeDescription'),
				import: true,
				type: 'String',
				importMatch: true,
				importDesc: t('tag.import.client.otherAddress.zip')
			},
			{
				name: t('tag.client.municipality.other'),
				tag: 'Client.OtherMunicipality',
				pdfTemplateTag: 'client.tagAddresses.Other.municipality',
				import: true,
				type: 'String',
				importMatch: true,
				importDesc: t('tag.import.client.otherAddress.municipality')
			},
			{
				name: t('tag.client.state.other'),
				tag: 'Client.OtherState',
				pdfTemplateTag: 'client.tagAddresses.Other.state',
				description: t('tag.account.stateDescription'),
				import: true,
				type: 'String',
				importMatch: true,
				importDesc: t('tag.import.client.otherAddress.state')
			},
			{
				name: t('tag.client.city.other'),
				tag: 'Client.OtherCity',
				pdfTemplateTag: 'client.tagAddresses.Other.city',
				description: t('tag.account.cityDescription'),
				import: true,
				type: 'String',
				importMatch: true,
				importDesc: t('tag.import.client.otherAddress.city')
			},
			{
				name: t('tag.client.country.other'),
				tag: 'Client.OtherCountry',
				pdfTemplateTag: 'client.tagAddresses.Other.country',
				description: t('tag.account.countryDescription'),
				import: true,
				type: 'String',
				importMatch: true,
				importDesc: t('tag.import.client.otherAddress.country')
			},

			{
				name: t('tag.account.phone'),
				tag: 'Client.Phone',
				pdfTemplateTag: 'client.phone',
				description: t('tag.account.phoneDescription'),
				import: true,
				type: 'String',
				importDesc: t('tag.import.client.phone')
			},
			{
				name: t('tag.account.fax'),
				tag: 'Client.Fax',
				pdfTemplateTag: 'client.fax',
				description: t('tag.account.faxDescription'),
				import: true,
				type: 'String',
				importDesc: t('tag.import.client.fax')
			},
			{
				name: t('tag.account.www'),
				tag: 'Client.Www',
				pdfTemplateTag: 'client.webpage',
				description: t('tag.account.wwwDescription'),
				import: true,
				type: 'String',
				importDesc: t('tag.import.client.website')
			},
			{
				name: t('tag.account.notes'),
				tag: 'Client.Notes',
				pdfTemplateTag: 'client.notes',
				description: t('tag.account.notesDescription'),
				import: true,
				type: 'String',
				importDesc: t('tag.import.client.notes')
			},
			{
				name: t('tag.account.score'),
				tag: 'Client.Score',
				pdfTemplateTag: 'client.score',
				description: t('tag.account.scoreDescription'),
				import: false,
				type: 'String'
			},

			{
				name: t('tag.account.userid'),
				tag: 'Client.UserId',
				description: t('tag.account.useridDescription'),
				import: false,
				type: 'String'
			},
			{
				name: t('tag.account.username'),
				tag: 'Client.UserName',
				description: t('tag.account.usernameDescription'),
				import: false,
				type: 'String'
			},
			{
				name: t('tag.account.useremail'),
				tag: 'Client.UserEmail',
				description: t('tag.account.useremailDescription'),
				import: false,
				type: 'String'
			},
			{
				name: t('tag.account.userphone'),
				tag: 'Client.UserPhone',
				description: t('tag.account.userphoneDescription'),
				import: false,
				type: 'String'
			},
			{
				name: t('tag.account.usercellphone'),
				tag: 'Client.UserCellPhone',
				description: t('tag.account.usercellphoneDescription'),
				import: false,
				type: 'String'
			},
			{
				name: t('tag.account.userrole'),
				tag: 'Client.UserRole',
				description: t('tag.account.userroleDescription'),
				import: false,
				type: 'String'
			},
			{
				name: t('tag.account.usertitle'),
				tag: 'Client.UserTitle',
				description: t('tag.account.usertitleDescription'),
				import: false,
				type: 'String'
			},
			{
				name: t('tag.account.dunsNo'),
				tag: 'Client.DunsNo',
				pdfTemplateTag: 'client.dunsNo',
				description: '',
				import: true,
				type: 'Integer',
				importMatch: true,
				importDesc: t('tag.import.client.duns')
			},
			{
				name: t('tag.account.regbyid'),
				tag: 'Client.RegById',
				pdfTemplateTag: 'client.regBy.id',
				description: t('tag.account.regbyidDescription'),
				import: false
			},
			{
				name: t('tag.account.regbyname'),
				tag: 'Client.RegByName',
				pdfTemplateTag: 'client.regBy.name',
				description: t('tag.account.regbynameDescription'),
				import: false
			},
			{
				name: t('tag.account.regbyemail'),
				tag: 'Client.RegByEmail',
				pdfTemplateTag: 'client.regBy.email',
				description: t('tag.account.regbyemailDescription'),
				import: false
			},
			{
				name: t('tag.account.active'),
				tag: 'Client.Active',
				pdfTemplateTag: 'client.active',
				description: t('tag.account.activeDescription'),
				import: false,
				type: 'Boolean'
			},
			{
				name: t('tag.account.assigneduserid'),
				tag: 'Client.AssignedUserId',
				pdfTemplateTag: 'client.assigned.user.id',
				description: t('tag.account.assigneduseridDescription'),
				import: false,
				type: 'Integer'
			},
			{
				name: t('tag.account.assignedusername'),
				tag: 'Client.AssignedUserName',
				pdfTemplateTag: 'client.assigned.user.name',
				description: t('tag.account.assignedusernameDescription'),
				import: false,
				type: 'String'
			},
			{
				name: t('tag.account.assigneduseremail'),
				tag: 'Client.AssignedUserEmail',
				pdfTemplateTag: 'client.assigned.user.email',
				description: t('tag.account.assigneduseremailDescription'),
				import: false,
				type: 'String'
			},
			{
				name: t('tag.account.assigneduserphone'),
				tag: 'Client.AssignedUserPhone',
				pdfTemplateTag: 'client.assigned.user.phone',
				description: t('tag.account.assigneduserphoneDescription'),
				import: false,
				type: 'String'
			},
			{
				name: t('tag.account.assigneduserrole'),
				tag: 'Client.AssignedUserRole',
				pdfTemplateTag: 'client.assigned.user.role.name',
				description: t('tag.account.assigneduserroleDescription'),
				import: false,
				type: 'String'
			},
			{
				name: t('tag.account.assignedusertitle'),
				tag: 'Client.AssignedUserTitle',
				pdfTemplateTag: 'client.assigned.user.title',
				description: t('tag.account.assignedusertitleDescription'),
				import: false,
				type: 'String'
			}
		].filter(Boolean),
		contact: [
			{
				name: t('tag.contact.id'),
				tag: 'Contact.Id',
				pdfTemplateTag: 'contact.id',
				description: t('tag.contact.idDescription'),
				import: true,
				type: 'Integer',
				importMatch: true,
				importDesc: t('tag.import.contact.id')
			},
			{
				name: t('tag.contact.name'),
				tag: 'Contact.Name',
				pdfTemplateTag: 'contact.name',
				description: t('tag.contact.nameDescription'),
				import: true,
				type: 'String',
				importMatch: true,
				importDesc: t('tag.import.contact.name')
			},
			{
				name: t('tag.contact.firstname'),
				tag: 'Contact.FirstName',
				pdfTemplateTag: 'contact.firstName',
				description: hasNewFields ? '' : t('tag.contact.firstnameDescription'),
				import: hasNewFields,
				type: 'String',
				importDesc: t('tag.import.contact.first')
			},
			{
				name: t('tag.contact.lastname'),
				tag: 'Contact.LastName',
				pdfTemplateTag: 'contact.lastName',
				description: hasNewFields ? '' : t('tag.contact.lastnameDescription'),
				import: hasNewFields,
				type: 'String',
				importDesc: t('tag.import.contact.last')
			},
			{
				name: t('tag.contact.phone'),
				tag: 'Contact.Phone',
				pdfTemplateTag: 'contact.phone',
				description: t('tag.contact.phoneDescription'),
				import: true,
				type: 'String',
				importMatch: true,
				importDesc: t('tag.import.contact.phone')
			},
			{
				name: t('tag.contact.cellphone'),
				tag: 'Contact.CellPhone',
				pdfTemplateTag: 'contact.cellPhone',
				description: t('tag.contact.cellphoneDescription'),
				import: true,
				type: 'String',
				importMatch: true,
				importDesc: t('tag.import.contact.mobile')
			},
			{
				name: t('tag.contact.email'),
				tag: 'Contact.Email',
				pdfTemplateTag: 'contact.email',
				description: t('tag.contact.emailDescription'),
				import: true,
				type: 'String',
				importMatch: true,
				importDesc: t('tag.import.contact.email')
			},
			{
				name: t('tag.contact.title'),
				tag: 'Contact.Title',
				pdfTemplateTag: 'contact.title',
				description: t('tag.contact.titleDescription'),
				import: true,
				type: 'String',
				importDesc: t('tag.import.contact.title')
			},
			{
				name: t('tag.contact.score'),
				tag: 'Contact.Score',
				pdfTemplateTag: 'contact.score',
				description: t('tag.contact.scoreDescription'),
				import: false,
				type: 'Integer'
			},
			{
				name: t('tag.contact.notes'),
				tag: 'Contact.Notes',
				pdfTemplateTag: 'contact.notes',
				description: t('tag.contact.notesDescription'),
				import: true,
				type: 'String',
				importDesc: t('tag.import.contact.notes')
			},
			{
				name: t('tag.contact.clientid'),
				tag: 'Contact.ClientId',
				description: t('tag.contact.clientidDescription'),
				import: false,
				type: 'Integer'
			},
			{
				name: t('tag.contact.regbyid'),
				tag: 'Contact.RegById',
				pdfTemplateTag: 'contact.regBy.id',
				description: t('tag.contact.regbyidDescription'),
				import: false
			},
			{
				name: t('tag.contact.regbyname'),
				tag: 'Contact.RegByName',
				pdfTemplateTag: 'contact.regBy.name',
				description: t('tag.contact.regbynameDescription'),
				import: false
			},
			{
				name: t('tag.contact.regbyemail'),
				tag: 'Contact.RegByEmail',
				pdfTemplateTag: 'contact.regBy.email',
				description: t('tag.contact.regbyemailDescription'),
				import: false
			},
			{
				name: t('tag.contact.active'),
				tag: 'Contact.Active',
				pdfTemplateTag: 'contact.regBy.active',
				description: t('tag.contact.activeDescription'),
				import: false,
				type: 'Boolean'
			}
		],
		activity: [
			{
				name: t('tag.activity.id'),
				tag: 'Activity.Id',
				description: t('tag.activity.idDescription'),
				type: 'Integer'
			},
			{
				name: t('tag.activity.activitytypeId'),
				tag: 'Activity.ActivityType',
				description: t('tag.activity.activitytypeIdDescription'),
				type: 'String'
			},
			{
				name: t('tag.activity.activitytype'),
				tag: 'ActivityType.Name',
				description: t('tag.activity.activitytypeDescription'),
				type: 'String'
			},
			{
				name: t('tag.activityDescription'),
				tag: 'Activity.Description',
				description: t('tag.activityDescriptionDescription'),
				type: 'String'
			},
			{
				name: t('tag.activity.notes'),
				tag: 'Activity.Notes',
				description: t('tag.activity.notesDescription'),
				type: 'String'
			},
			{
				name: t('tag.activity.date'),
				tag: 'Activity.Date',
				description: t('tag.activity.dateDescription'),
				type: 'Date'
			},
			{
				name: t('tag.activity.closedate'),
				tag: 'Activity.CloseDate',
				description: t('tag.activity.closedateDescription'),
				type: 'Date'
			},
			{
				name: t('tag.activity.userid'),
				tag: 'Activity.UserId',
				description: t('tag.activity.useridDescription'),
				type: 'String'
			},
			{
				name: t('tag.activity.username'),
				tag: 'Activity.UserName',
				description: t('tag.activity.usernameDescription'),
				type: 'String'
			},
			{
				name: t('tag.activity.useremail'),
				tag: 'Activity.UserEmail',
				description: t('tag.activity.useremailDescription'),
				type: 'String'
			},
			{
				name: t('tag.activity.clientid'),
				tag: 'Activity.ClientId',
				description: t('tag.activity.clientidDescription'),
				type: 'Integer'
			},
			{
				name: t('tag.activity.contactid'),
				tag: 'Activity.ContactId',
				description: t('tag.activity.contactidDescription'),
				type: 'Integer'
			},
			{
				name: t('tag.activity.order'),
				tag: 'Activity.Order',
				description: t('tag.activity.orderDescription'),
				type: 'String'
			},
			{
				name: t('tag.activity.projectid'),
				tag: 'Activity.ProjectId',
				description: t('tag.activity.projectidDescription'),
				type: 'Integer'
			},
			{
				name: t('tag.activity.regbyid'),
				tag: 'Activity.RegById',
				description: t('tag.activity.regbyidDescription')
			},
			{
				name: t('tag.activity.regbyname'),
				tag: 'Activity.RegByName',
				description: t('tag.activity.regbynameDescription')
			},
			{
				name: t('tag.activity.regbyemail'),
				tag: 'Activity.RegByEmail',
				description: t('tag.activity.regbyemailDescription')
			},
			{
				name: t('tag.activity.active'),
				tag: 'Activity.Active',
				description: t('tag.activity.activeDescription'),
				type: 'Boolean'
			}
		],
		appointment: [
			{
				name: t('tag.appointment.id'),
				tag: 'Appointment.Id',
				description: '',
				type: 'Integer'
			},
			{
				name: t('tag.appointmentDescription'),
				tag: 'Appointment.Description',
				description: '',
				type: 'String'
			},
			{
				name: t('tag.appointment.appointmenttypeId'),
				tag: 'Appointment.AppointmentType',
				description: t('tag.appointment.appointmenttypeIdDescription'),
				type: 'String'
			},
			{
				name: t('tag.appointment.appointmenttype'),
				tag: 'AppointmentType.Name',
				description: t('tag.appointment.appointmenttypeDescription'),
				type: 'String'
			},
			{
				name: t('tag.appointment.notes'),
				tag: 'Appointment.Notes',
				description: '',
				type: 'String'
			},
			{
				name: t('tag.appointment.date'),
				tag: 'Appointment.Date',
				description: '',
				type: 'String'
			},
			{
				name: t('default.startTime'),
				tag: 'Appointment.StartTime',
				description: '',
				type: 'String'
			},
			{
				name: t('default.endTime'),
				tag: 'Appointment.EndTime',
				description: '',
				type: 'String'
			},
			{
				name: t('tag.appointment.userid'),
				tag: 'Appointment.UserId',
				description: '',
				type: 'Integer'
			},
			{
				name: t('tag.appointment.clientid'),
				tag: 'Appointment.ClientId',
				description: '',
				type: 'Integer'
			},
			{
				name: t('tag.appointment.contactid'),
				tag: 'Appointment.ContactId',
				description: '',
				type: 'Integer'
			},
			{
				name: t('tag.appointment.order'),
				tag: 'Appointment.Order',
				description: '',
				type: 'String'
			},
			{
				name: t('tag.appointment.projectid'),
				tag: 'Appointment.ProjectId',
				description: '',
				type: 'Integer'
			},
			{
				name: t('tag.appointment.regbyid'),
				tag: 'Appointment.RegById',
				description: ''
			},
			{
				name: t('tag.appointment.regbyname'),
				tag: 'Appointment.RegByName',
				description: ''
			},
			{
				name: t('tag.appointment.regbyemail'),
				tag: 'Appointment.RegByEmail',
				description: ''
			},
			{
				name: t('tag.appointment.participantid'),
				tag: 'Appointment.ParticipantId',
				description: ''
			},
			{
				name: t('tag.appointment.participantsnames'),
				tag: 'Appointment.ParticipantsNames',
				description: '',
				type: 'String'
			},
			{
				name: t('tag.appointment.participantsemails'),
				tag: 'Appointment.ParticipantsEmails',
				description: '',
				type: 'String'
			},
			{
				name: t('tag.client.contact.email'),
				tag: 'Appointment.ContactsEmails',
				description: '',
				type: 'String'
			}
		],
		order: [
			{
				name: t('tag.order.id'),
				tag: 'Order.Id',
				pdfTemplateTag: 'order.id',
				description: t('tag.activity.idDescription'),
				type: 'Integer'
			},
			{
				name: t('tag.orderDescription'),
				tag: 'Order.Description',
				pdfTemplateTag: 'order.description',
				description: t('tag.activity.activitytypeDescription'),
				type: 'String'
			},
			{
				name: t('tag.order.stage'),
				tag: 'Order.Stage',
				pdfTemplateTag: 'order.stage.name',
				description: t('tag.activityDescriptionDescription'),
				type: 'String'
			},
			{
				name: t('tag.order.date'),
				tag: 'Order.Date',
				pdfTemplateTag: 'order.date',
				description: t('tag.activity.notesDescription'),
				type: 'Date'
			},
			{
				name: t('tag.order.notes'),
				tag: 'Order.Notes',
				pdfTemplateTag: 'order.notes',
				description: t('tag.activity.dateDescription'),
				type: 'String'
			},
			{
				name: t('tag.order.userid'),
				tag: 'Order.UserId',
				pdfTemplateTag: 'order.user.id',
				description: t('tag.activity.closedateDescription'),
				type: 'Integer'
			},
			{
				name: t('tag.order.username'),
				tag: 'Order.UserName',
				pdfTemplateTag: 'order.user.name',
				description: t('tag.activity.useridDescription'),
				type: 'String'
			},
			{
				name: t('tag.order.useremail'),
				tag: 'Order.UserEmail',
				pdfTemplateTag: 'order.user.email',
				description: t('tag.activity.usernameDescription'),
				type: 'String'
			},
			{
				name: t('tag.order.clientid'),
				tag: 'Order.ClientId',
				pdfTemplateTag: 'order.client.id',
				description: t('tag.activity.useremailDescription'),
				type: 'Integer'
			},
			{
				name: t('tag.order.contactid'),
				tag: 'Order.ContactId',
				pdfTemplateTag: 'order.contact.id',
				description: t('tag.activity.clientidDescription'),
				type: 'Integer'
			},
			{
				name: t('tag.order.projectid'),
				tag: 'Order.ProjectId',
				pdfTemplateTag: 'order.project.id',
				description: t('tag.activity.contactidDescription'),
				type: 'Integer'
			},
			{
				name: t('tag.order.orderedproduct'),
				tag: 'Order.OrderedProduct',
				description: t('tag.activity.orderDescription'),
				type: 'String'
			},
			{
				name: t('tag.order.orderedproductName'),
				tag: 'OrderedProduct.Name',
				description: t('tag.order.orderedproductNameDescription'),
				type: 'String'
			},
			{
				name: t('tag.order.probability'),
				tag: 'Order.Probability',
				pdfTemplateTag: 'order.probability',
				description: t('tag.activity.projectidDescription'),
				type: 'Integer'
			},
			{
				name: t('tag.order.currency'),
				tag: 'Order.Currency',
				pdfTemplateTag: 'order.currency',
				description: t('tag.activity.regbyidDescription'),
				type: 'String'
			},
			{
				name: t('tag.order.ordervalue'),
				tag: 'Order.OrderValue',
				pdfTemplateTag: 'order.orderValue',
				description: t('tag.activity.regbynameDescription'),
				type: 'Integer'
			},
			{
				name: t('tag.order.locked'),
				tag: 'Order.Locked',
				pdfTemplateTag: 'order.locked',
				description: t('tag.activity.regbyemailDescription'),
				type: 'Boolean'
			},
			{
				name: t('tag.order.genereatedfromagreement'),
				tag: 'Order.genereatedFromAgreement',
				pdfTemplateTag: 'order.agreement.metadata.agreementDescription',
				description: t('tag.activity.activeDescription'),
				type: 'Boolean'
			}
		],
		opportunity: [],
		userdefinedobject: [],
		userdefinedobject2: [],
		userdefinedobject3: [],
		userdefinedobject4: [],
		project: [
			{
				name: t('tag.project.id'),
				tag: 'Project.Id',
				pdfTemplateTag: 'project.id',
				description: t('tag.project.idDescription'),
				type: 'Integer'
			},
			{
				name: t('tag.project.name'),
				tag: 'Project.Name',
				pdfTemplateTag: 'project.name',
				description: t('tag.project.nameDescription'),
				type: 'String'
			},
			{
				name: t('tag.project.startdate'),
				tag: 'Project.StartDate',
				pdfTemplateTag: 'project.startDate',
				description: t('tag.project.startdateDescription'),
				type: 'Date'
			},
			{
				name: t('tag.project.enddate'),
				tag: 'Project.EndDate',
				pdfTemplateTag: 'project.endDate',
				description: t('tag.project.enddateDescription'),
				type: 'Date'
			},
			{
				name: t('tag.project.active'),
				tag: 'Project.Active',
				pdfTemplateTag: 'project.active',
				description: t('tag.project.activeDescription'),
				type: 'String'
			},
			{
				name: t('tag.project.quota'),
				tag: 'Project.Quota',
				pdfTemplateTag: 'project.quota',
				description: t('tag.project.quotaDescription'),
				type: 'Integer'
			},
			{
				name: t('tag.project.notes'),
				tag: 'Project.Notes',
				pdfTemplateTag: 'project.notes',
				description: t('tag.project.notesDescription'),
				type: 'String'
			}
		],
		agreement: [
			{
				name: t('tag.agreement.id'),
				tag: 'Agreement.Id',
				description: t('tag.agreement.idDescription'),
				type: 'Integer'
			},
			{
				name: t('tag.agreementDescription'),
				tag: 'Agreement.Description',
				description: t('tag.agreementDescriptionDescription'),
				type: 'String'
			},
			{
				name: t('tag.agreement.agreementnote'),
				tag: 'Agreement.AgreementNote',
				description: t('tag.agreement.agreementnoteDescription'),
				type: 'String'
			},
			{
				name: t('tag.agreement.stage'),
				tag: 'Agreement.Stage',
				description: t('tag.agreement.stageDescription'),
				type: 'String'
			},
			{
				name: t('tag.agreement.userid'),
				tag: 'Agreement.UserId',
				description: t('tag.agreement.useridDescription'),
				type: 'Integer'
			},
			{
				name: t('tag.agreement.username'),
				tag: 'Agreement.UserName',
				description: t('tag.agreement.usernameDescription'),
				type: 'String'
			},
			{
				name: t('tag.agreement.useremail'),
				tag: 'Agreement.UserEmail',
				description: t('tag.agreement.useremailDescription'),
				type: 'String'
			},
			{
				name: t('tag.agreement.clientid'),
				tag: 'Agreement.ClientId',
				description: t('tag.agreement.clientidDescription'),
				type: 'Integer'
			},
			{
				name: t('tag.agreement.contactid'),
				tag: 'Agreement.ContactId',
				description: t('tag.agreement.contactidDescription'),
				type: 'Integer'
			},
			{
				name: t('tag.agreement.projectid'),
				tag: 'Agreement.ProjectId',
				description: t('tag.agreement.projectidDescription'),
				type: 'Integer'
			},
			{
				name: t('tag.agreement.currency'),
				tag: 'Agreement.Currency',
				description: t('tag.agreement.currencyDescription'),
				type: 'String'
			},
			{
				name: t('tag.agreement.currencyrate'),
				tag: 'Agreement.CurrencyRate',
				description: t('tag.agreement.currencyrateDescription'),
				type: 'Integer'
			},
			{
				name: t('tag.agreement.agreementdescription'),
				tag: 'Agreement.AgreementDescription',
				description: t('tag.agreement.agreementdescriptionDescription'),
				type: 'String'
			},
			{
				name: t('tag.agreement.agreementstartdate'),
				tag: 'Agreement.AgreementStartdate',
				description: t('tag.agreement.agreementstartdateDescription'),
				type: 'Date'
			},
			{
				name: t('tag.agreement.agreementrenewaldate'),
				tag: 'Agreement.AgreementRenewalDate',
				description: t('tag.agreement.agreementrenewaldateDescription'),
				type: 'Date'
			},
			{
				name: t('tag.agreement.agreementenddate'),
				tag: 'Agreement.AgreementEnddate',
				description: t('tag.agreement.agreementenddateDescription'),
				type: 'Date'
			},
			{
				name: t('tag.agreement.agreementnextorderdate'),
				tag: 'Agreement.AgreementNextOrderDate',
				description: t('tag.agreement.agreementnextorderdateDescription'),
				type: 'Date'
			},
			{
				name: t('tag.agreement.agreementintervalperiod'),
				tag: 'Agreement.AgreementIntervalPeriod',
				description: t('tag.agreement.agreementintervalperiodDescription'),
				type: 'Integer'
			},
			{
				name: t('tag.agreement.agreementinvoicestartdate'),
				tag: 'Agreement.AgreementInvoiceStartdate',
				description: t('tag.agreement.agreementinvoicestartdateDescription'),
				type: 'Date'
			},
			{
				name: t('tag.agreement.ordercreationtime'),
				tag: 'Agreement.OrderCreationTime',
				description: t('tag.agreementordercreationtime.Description'),
				type: 'String'
			}
		],
		form: [
			{
				name: t('tag.form.id'),
				tag: 'Form.Id',
				description: t('tag.form.idDescription'),
				type: 'Integer'
			},
			{
				name: t('tag.form.title'),
				tag: 'Form.Title',
				description: t('tag.form.titleDescription'),
				type: 'String'
			},
			{
				name: t('tag.form.score'),
				tag: 'Form.Score',
				description: t('tag.form.scoreDescription'),
				type: 'Integer'
			}
		],
		landingpage: [],
		formsubmit: [
			{
				name: t('tag.formsubmit.id'),
				tag: 'FormSubmit.Id',
				description: t('tag.formsubmit.idDescription'),
				type: 'Integer'
			},
			{
				name: t('tag.formsubmit.client'),
				tag: 'FormSubmit.Client',
				description: t('tag.formsubmit.clientDescription'),
				type: 'String'
			},
			{
				name: t('tag.formsubmit.contact'),
				tag: 'FormSubmit.Contact',
				description: t('tag.formsubmit.contactDescription'),
				type: 'String'
			},
			{
				name: t('tag.formsubmit.form'),
				tag: 'FormSubmit.Form',
				description: t('tag.formsubmit.formDescription'),
				type: 'String'
			}
		],
		mailspecial: [
			{
				name: t('tag.mailspecial.unsub'),
				tag: 'UNSUB',
				description: t('tag.mailspecial.unsubDescription')
			}
		],
		generalWithoutUserAndCompany: [
			{
				name: t('tag.general.today'),
				tag: 'General.Today',
				pdfTemplateTag: 'general.Today',
				description: t('tag.general.todayDescription'),
				type: 'Date'
			},
			{
				name: t('tag.general.clearfield'),
				tag: 'General.ClearField',
				pdfTemplateTag: 'general.ClearField',
				description: t('tag.general.clearfield'),
				type: 'String'
			},
			{
				name: t('tag.general.currentyear'),
				tag: 'General.CurrentYear',
				pdfTemplateTag: 'general.CurrentYear',
				description: t('tag.general.currentyearDescription'),
				type: 'String'
			}
		],
		general: [
			{
				name: t('tag.general.today'),
				tag: 'General.Today',
				pdfTemplateTag: 'general.Today',
				description: t('tag.general.todayDescription'),
				type: 'Date'
			},
			{
				name: t('tag.general.clearfield'),
				tag: 'General.ClearField',
				pdfTemplateTag: 'general.ClearField',
				description: t('tag.general.clearfield'),
				type: 'String'
			},
			{
				name: t('tag.general.currentyear'),
				tag: 'General.CurrentYear',
				pdfTemplateTag: 'general.CurrentYear',
				description: t('tag.general.currentyearDescription'),
				type: 'String'
			},
			{
				name: t('tag.general.currentuserid'),
				tag: 'General.CurrentUserId',
				pdfTemplateTag: 'general.CurrentUserId',
				description: t('tag.general.currentuseridDescription'),
				type: 'Integer'
			},
			{
				name: t('tag.general.currentuserroleid'),
				tag: 'General.CurrentUserRoleId',
				pdfTemplateTag: 'general.CurrentUserRoleId',
				description: t('tag.general.currentuserroleidDescription'),
				type: 'Integer'
			},
			{
				name: t('tag.general.currentusername'),
				tag: 'General.CurrentUserName',
				pdfTemplateTag: 'general.CurrentUserName',
				description: t('tag.general.currentusernameDescription'),
				type: 'String'
			},
			{
				name: t('tag.general.currentuseremail'),
				tag: 'General.CurrentUserEmail',
				pdfTemplateTag: 'general.CurrentUserEmail',
				description: t('tag.general.currentuseremailDescription'),
				type: 'String'
			},
			{
				name: t('tag.general.currentuserphone'),
				tag: 'General.CurrentUserPhone',
				pdfTemplateTag: 'general.CurrentUserPhone',
				description: t('tag.general.currentuserphoneDescription'),
				type: 'String'
			},
			{
				name: t('tag.general.currentusercellphone'),
				tag: 'General.CurrentUserCellPhone',
				pdfTemplateTag: 'general.CurrentUserCellPhone',
				description: t('tag.general.currentusercellphoneDescription'),
				type: 'String'
			},
			{
				name: t('tag.general.currentusertitle'),
				tag: 'General.CurrentUserTitle',
				pdfTemplateTag: 'general.CurrentUserTitle',
				description: t('tag.general.currentusertitleDescription'),
				type: 'String'
			},
			{
				name: t('tag.general.companyname'),
				tag: 'General.CompanyName',
				pdfTemplateTag: 'general.CompanyName',
				description: t('tag.general.companynameDescription'),
				type: 'String'
			},
			{
				name: t('tag.general.companyphone'),
				tag: 'General.CompanyPhone',
				pdfTemplateTag: 'general.CompanyPhone',
				description: t('tag.general.companyphoneDescription'),
				type: 'String'
			},
			{
				name: t('tag.general.companyaddress'),
				tag: 'General.CompanyAddress',
				pdfTemplateTag: 'general.CompanyAddress',
				description: t('tag.general.companyaddressDescription'),
				type: 'String'
			},
			{
				name: t('tag.general.companyzipcode'),
				tag: 'General.CompanyZipCode',
				pdfTemplateTag: 'general.CompanyZipCode',
				description: t('tag.general.companyzipcodeDescription'),
				type: 'String'
			},
			{
				name: t('tag.general.companycity'),
				tag: 'General.CompanyCity',
				pdfTemplateTag: 'general.CompanyCity',
				description: t('tag.general.companycityDescription'),
				type: 'String'
			},
			{
				name: t('tag.general.companycountry'),
				tag: 'General.CompanyCountry',
				pdfTemplateTag: 'general.CompanyCountry',
				description: t('tag.general.companycountryDescription'),
				type: 'String'
			},
			{
				name: t('tag.general.companydisclaimer'),
				tag: 'General.CompanyDisclaimer',
				pdfTemplateTag: 'general.CompanyDisclaimer',
				description: t('tag.general.companydisclaimerDescription'),
				type: 'String'
			},
			{
				name: t('tag.general.companyemail'),
				tag: 'General.CompanyEmail',
				pdfTemplateTag: 'general.CompanyEmail',
				description: t('tag.general.companyemailDescription'),
				type: 'String'
			},
			{
				name: t('tag.general.companysender'),
				tag: 'General.CompanySender',
				pdfTemplateTag: 'general.CompanySender',
				description: t('tag.general.companysenderDescription'),
				type: 'String'
			}
		],
		product: [
			{
				name: t('tag.product.name'),
				tag: 'Product.Name',
				pdfTemplateTag: 'product.name',
				description: t('tag.product.nameDescription'),
				import: true,
				type: 'String',
				importMatch: true
			},
			{
				name: t('tag.product.price'),
				tag: 'Product.Price',
				pdfTemplateTag: 'product.price',
				description: t('tag.product.priceDescription'),
				import: true,
				type: 'Integer'
			},
			{
				name: t('tag.product.active'),
				tag: 'Product.Active',
				pdfTemplateTag: 'product.active',
				description: t('tag.product.activeDescription'),
				import: true,
				type: 'String',
				importMatch: true
			},
			{
				name: t('tag.product.purchasePrice'),
				tag: 'Product.PurchasePrice',
				pdfTemplateTag: 'product.purchasePrice',
				description: t('tag.product.purchasePriceDescription'),
				import: true,
				type: 'Integer'
			},
			{
				name: t('tag.product.id'),
				tag: 'Product.Id',
				pdfTemplateTag: 'product.id',
				description: t('tag.product.idDescription'),
				import: true,
				type: 'Integer',
				importMatch: true
			},
			{
				name: t('tag.product.category'),
				tag: 'Product.CategoryName',
				pdfTemplateTag: 'product.category',
				description: t('tag.product.categoryDescription'),
				import: true,
				type: 'String',
				importMatch: true
			},
			{
				name: t('tag.product.isRecurring'),
				tag: 'Product.IsRecurring',
				pdfTemplateTag: 'product.isRecurring',
				description: t('tag.product.isRecurringDescription'),
				import: true,
				type: 'String'
			},
			{
				name: t('tag.product.description'),
				tag: 'Product.Description',
				pdfTemplateTag: 'product.description',
				description: t('tag.product.descriptionDescription'),
				import: true,
				type: 'String'
			},
			{
				name: t('tag.product.sortId'),
				tag: 'Product.SortId',
				pdfTemplateTag: 'product.sortId',
				description: t('tag.product.sortIdDescription'),
				import: true,
				type: 'Integer'
			}
		],
		ticket: [
			{
				name: t('tag.ticket.id'),
				tag: 'Ticket.Id',
				description: t('tag.ticket.idDescription'),
				type: 'Integer'
			},
			{
				name: t('tag.ticket.title'),
				tag: 'Ticket.Title',
				description: t('tag.ticket.titleDescription'),
				type: 'String'
			},
			{
				name: t('tag.ticket.type'),
				tag: 'Ticket.TypeName',
				description: t('tag.ticket.typeDescription'),
				type: 'String'
			}
		],
		clientrelation: [],
		mail: [],
		sidebar: [],
		visit: [],
		projectplan: [
			{
				name: t('tag.projectPlan.id'),
				tag: 'ProjectPlan.Id',
				description: t('tag.projectPlan.idDescription'),
				type: 'Integer'
			},
			{
				name: t('tag.projectPlan.name'),
				tag: 'ProjectPlan.Name',
				description: t('tag.projectPlan.nameDescription'),
				type: 'String'
			},
			{
				name: t('tag.projectPlan.startdate'),
				tag: 'ProjectPlan.StartDate',
				description: t('tag.projectPlan.startdateDescription'),
				type: 'Date'
			},
			{
				name: t('tag.projectPlan.enddate'),
				tag: 'ProjectPlan.EndDate',
				description: t('tag.projectPlan.enddateDescription'),
				type: 'Date'
			},
			{
				name: t('tag.projectPlan.notes'),
				tag: 'ProjectPlan.Notes',
				description: t('tag.projectPlan.notesDescription'),
				type: 'String'
			},
			{
				name: t('tag.projectPlan.stage'),
				tag: 'ProjectPlan.StageName',
				description: t('tag.projectPlan.stageDescription'),
				type: 'String'
			},
			{
				name: t('tag.projectPlan.userid'),
				tag: 'ProjectPlan.UserId',
				description: t('tag.projectPlan.useridDescription'),
				type: 'Integer'
			},
			{
				name: t('tag.projectPlan.username'),
				tag: 'ProjectPlan.UserName',
				description: t('tag.projectPlan.usernameDescription'),
				type: 'String'
			},
			{
				name: t('tag.projectPlan.useremail'),
				tag: 'ProjectPlan.UserEmail',
				description: t('tag.projectPlan.useremailDescription'),
				type: 'String'
			}
		],
		projectplantemplate: [],
		tasktemplate: []
	};
	tags.todo = tags.activity;
	tags.phonecall = tags.activity;
	tags.account = tags.client;

	tags.client.push({
		name: t('tag.account.priceList'),
		tag: 'Client.PriceList',
		pdfTemplateTag: 'client.priceList.code',
		description: t('tag.account.priceListDescription'),
		import: true,
		type: 'String',
		importDesc: t('tag.account.priceListDescription')
	});

	const index = tags.general.findIndex(tag => tag.tag === 'General.CurrentUserTitle');
	tags.general.splice(index + 1, 0, {
		name: t('tag.general.currentusermailsignature'),
		tag: 'General.CurrentUserMailSignature',
		description: t('tag.general.currentusermailsignatureDescription'),
		type: 'String'
	});

	if (hasNewFields) {
		const ClientFields = AppService.getMetadata().standardFields.Client;
		const contactFields = AppService.getMetadata().standardFields.Contact;

		if (contactFields.Salutation && contactFields.Salutation.active) {
			tags.contact.push({
				name: t('tag.contact.salutationCode'),
				tag: 'Contact.Salutation',
				description: t('tag.contact.salutationDescription'),
				import: true,
				type: 'String'
			});
			tags.contact.push({
				name: t('tag.contact.salutation'),
				tag: 'Contact.SalutationValue',
				description: t('tag.contact.salutationDescription'),
				import: false,
				type: 'String'
			});
		}

		const ProductFields = AppService.getMetadata().standardFields.Product;

		if (ProductFields.ArticleNo?.active) {
			tags.product.push({
				name: t('product.articleNo'),
				tag: 'Product.ArticleNo',
				description: t('product.articleNo.description'),
				import: true,
				type: 'String',
				importMatch: true
			});
		}

		const standardFieldTags: EntityTag[] = [];
		standardFieldTags.push({
			name: t('tag.account.orgNo'),
			tag: 'Client.OrgNo',
			pdfTemplateTag: 'client.orgNo',
			description: t('tag.account.orgNoDescription'),
			import: true,
			type: 'String',
			importMatch: true,
			importDesc: t('tag.import.client.orgNo')
		});
		standardFieldTags.push({
			name: t('tag.account.registrationDate'),
			tag: 'Client.RegistrationDate',
			pdfTemplateTag: 'client.regDate',
			description: t('tag.account.registrationDateDescription'),
			import: true,
			type: 'Date',
			importDesc: t('tag.import.client.created')
		});

		standardFieldTags.push({
			name: t('tag.account.sniCode'),
			tag: 'Client.SniCodeValue',
			description: t('tag.account.sniCodeDescription'),
			import: false,
			type: 'String'
		});
		standardFieldTags.push({
			name: t('tag.account.sicCode'),
			tag: 'Client.SicCodeValue',
			description: t('tag.account.sicCodeDescription'),
			import: false,
			type: 'String'
		});
		standardFieldTags.push({
			name: t('tag.account.naicsCode'),
			tag: 'Client.NaicsCodeValue',
			description: t('tag.account.naicsCodeDescription'),
			import: false,
			type: 'String'
		});
		standardFieldTags.push({
			name: t('default.companyBranch.uksic'),
			tag: 'Client.UKSicCodeValue',
			description: t('tag.account.uksicCodeDescription'),
			import: false,
			type: 'String'
		});
		standardFieldTags.push({
			name: t('default.companyBranch.nace'),
			tag: 'Client.NaceCodeValue',
			description: t('tag.account.naceCodeDescription'),
			import: false,
			type: 'String'
		});
		standardFieldTags.push({
			name: t('tag.account.status'),
			tag: 'Client.StatusValue',
			description: t('tag.account.statusDescription'),
			import: false,
			type: 'String'
		});
		standardFieldTags.push({
			name: t('tag.account.companyForm'),
			tag: 'Client.CompanyFormValue',
			description: t('tag.account.companyFormDescription'),
			import: false,
			type: 'String'
		});
		standardFieldTags.push({
			name: t('tag.account.creditRating'),
			tag: 'Client.CreditRatingValue',
			pdfTemplateTag: 'client.creditRating',
			description: t('tag.account.creditRatingDescription'),
			import: false,
			type: 'String'
		});
		standardFieldTags.push({
			name: t('tag.account.sniCodeCode'),
			tag: 'Client.SniCode',
			pdfTemplateTag: 'client.sniCode',
			description: t('tag.account.sniCodeDescription'),
			import: true,
			type: 'String',
			importDesc: t('tag.import.client.industrySNI')
		});
		standardFieldTags.push({
			name: t('tag.account.naceCode'),
			tag: 'Client.NaceCode',
			pdfTemplateTag: 'client.naceCode',
			description: t('tag.account.sniCodeDescription'),
			import: true,
			type: 'String',
			importDesc: t('tag.import.client.industryNACE')
		});
		standardFieldTags.push({
			name: t('tag.account.sicCodeCode'),
			tag: 'Client.SicCode',
			pdfTemplateTag: 'client.sicCode',
			description: t('tag.account.sicCodeDescription'),
			import: true,
			type: 'String',
			importDesc: t('tag.import.client.industrySIC')
		});
		standardFieldTags.push({
			name: t('tag.account.naicsCodeCode'),
			tag: 'Client.NaicsCode',
			pdfTemplateTag: 'client.naicsCode',
			description: t('tag.account.naicsCodeDescription'),
			import: true,
			type: 'String',
			importDesc: t('tag.import.client.industryNAICS')
		});
		standardFieldTags.push({
			name: t('tag.account.statusCode'),
			tag: 'Client.Status',
			pdfTemplateTag: 'client.status',
			description: t('tag.account.statusDescription'),
			import: true,
			type: 'String',
			importDesc: t('tag.import.client.status')
		});
		standardFieldTags.push({
			name: t('tag.account.companyFormCode'),
			tag: 'Client.CompanyForm',
			description: t('tag.account.companyFormDescription'),
			import: true,
			type: 'String',
			importDesc: t('tag.import.client.corporateForm')
		});
		standardFieldTags.push({
			name: t('tag.account.creditRatingCode'),
			tag: 'Client.CreditRating',
			pdfTemplateTag: 'client.creditRating',
			description: t('tag.account.creditRatingDescription'),
			import: true,
			type: 'String',
			importDesc: t('tag.import.client.creditRating')
		});

		standardFieldTags.push({
			name: t('tag.account.turnover'),
			tag: 'Client.Turnover',
			pdfTemplateTag: 'client.turnover',
			description: t('tag.account.turnoverDescription'),
			import: true,
			type: 'Integer',
			importDesc: t('tag.import.client.turnover')
		});
		standardFieldTags.push({
			name: t('tag.account.profit'),
			tag: 'Client.Profit',
			pdfTemplateTag: 'client.profit',
			description: t('tag.account.profitDescription'),
			import: true,
			type: 'Integer',
			importDesc: t('tag.import.client.profit')
		});
		standardFieldTags.push({
			name: t('tag.account.noEmployees'),
			tag: 'Client.NoEmployees',
			pdfTemplateTag: 'client.noEmployees',
			description: t('tag.account.noEmployeesDescription'),
			import: true,
			type: 'Integer',
			importDesc: t('tag.import.client.noEmployees')
		});
		standardFieldTags.push({
			name: t('tag.account.facebook'),
			tag: 'Client.Facebook',
			pdfTemplateTag: 'client.facebook',
			description: t('tag.account.facebookDescription'),
			import: true,
			type: 'String',
			importDesc: t('tag.import.client.facebook')
		});
		standardFieldTags.push({
			name: t('tag.account.twitter'),
			tag: 'Client.Twitter',
			pdfTemplateTag: 'client.twitter',
			description: t('tag.account.twitterDescription'),
			import: true,
			type: 'String',
			importDesc: t('tag.import.client.twitter')
		});
		standardFieldTags.push({
			name: t('tag.account.linkedIn'),
			tag: 'Client.LinkedIn',
			pdfTemplateTag: 'client.linkedin',
			description: t('tag.account.linkedInDescription'),
			import: true,
			type: 'String',
			importDesc: t('tag.import.client.linkedin')
		});

		standardFieldTags.forEach(function (tag) {
			const fieldName = tag.tag.split('.')[1];
			const field = ClientFields[fieldName] || ClientFields[fieldName.split('Value')[0]];
			const isActive = field && field.active;
			if (isActive || fieldName === 'OrgNo') {
				tags.client.splice(32, 0, tag);
			}
		});

		const ContactFields = AppService.getMetadata().standardFields.Contact;

		if (ContactFields.LinkedIn) {
			tags.contact.push({
				name: t('tag.account.linkedIn'),
				tag: 'Contact.LinkedIn',
				pdfTemplateTag: 'contact.linkedin',
				description: t('tag.account.linkedInDescription'),
				import: true,
				type: 'String',
				importDesc: t('tag.import.contact.linkedin')
			});
		}
	}

	const getUdoRelations = (id: number): string[] => {
		const metadata = AppService.getMetadata();
		const udos = metadata.params.UserDefinedObject as { id: number; linkType: string }[];
		const udo = (udos ?? []).find(u => u.id === id);
		if (!udo) {
			return [];
		}

		const relations = [udo.linkType];
		if (udo.linkType === 'contact') {
			relations.push('client');
		}
		return relations;
	};

	const tagsByEntity: TagsByEntityMap = {
		general: ['general'],
		client: ['general'],
		contact: ['client', 'general'],
		activity: ['contact', 'client', 'project', 'general'],
		todo: ['contact', 'client', 'project', 'general'],
		phonecall: ['contact', 'client', 'project', 'general'],
		appointment: ['contact', 'client', 'project', 'general'],
		order: ['contact', 'client', 'project', 'general'],
		opportunity: ['contact', 'client', 'project', 'general'],
		project: ['general'],
		form: ['contact', 'client', 'form', 'generalWithoutUserAndCompany'],
		landingpage: ['general'],
		clientrelation: ['client', 'general'],
		formsubmit: ['contact', 'client', 'form', 'generalWithoutUserAndCompany'],
		mailcampaign: ['mailspecial', 'contact', 'client', 'project', 'general'],
		mail: ['mailspecial', 'contact', 'client', 'general'],
		user: ['general'],
		sidebar: ['general'],
		agreement: ['client', 'contact', 'project', 'general'],
		visit: ['general'],
		product: ['general'],
		projectplantemplate: ['client', 'order', 'general'],
		tasktemplate: ['projectplan', 'client', 'order', 'general'],
		projectplan: ['contact', 'client', 'general'],
		ticket: ['client', 'contact', 'general'],
		userdefinedobject: getUdoRelations(1),
		userdefinedobject2: getUdoRelations(2),
		userdefinedobject3: getUdoRelations(3),
		userdefinedobject4: getUdoRelations(4)
	};

	cached = { tags, tagsByEntity };
	return cached;
};

/*
	Port of TagsService.getTagsByEntity. Two pre-existing quirks are deliberately kept:

	- it MUTATES the cached catalogue entries (tag.entity = entity) behind only a shallow copy of the
	  entity list, so the catalogue is dirtied across calls. Consumers read tag.entity, so the write
	  is load-bearing rather than accidental.
	- it throws for any entity with no catalogue row, because the entity is looked up unconditionally
	  after being unshifted onto its (possibly empty) relation list. That covers both unknown names
	  and mailcampaign, which has a tagsByEntity row but no tags row. No caller passes either; the
	  Angular original threw the same way. todo and phonecall are safe — they are aliased onto the
	  activity tags when the catalogue is built.
*/
export const getEntityTags = (mainEntity: string): EntityTag[] => {
	const { tags, tagsByEntity } = buildTags();
	const t: (EntityTag | EntityTag[])[] = [];
	mainEntity = mainEntity.toLowerCase();
	const entities = tagsByEntity[mainEntity] ? [...tagsByEntity[mainEntity]] : [];
	entities.unshift(mainEntity);

	entities.forEach(function (entity) {
		t.push(
			tags[entity].map(function (tag) {
				tag.entity = entity;
				return tag;
			})
		);
		if (nameMapper.hasOwnProperty(entity)) {
			const customFieldEntity = customFieldMapper[entity] ?? entity;
			const fields = AppService.getCustomFields(customFieldEntity as CustomFieldKey);
			fields.forEach(function (cf: CustomField) {
				t.push({
					name: cf.name,
					tag: nameMapper[entity] + '.custom_' + cf.id,
					pdfTemplateTag: `custom.${nameMapper[entity].toLowerCase()}.${cf.id}`,
					tagId: cf.id,
					nameMapper: nameMapper[entity].toLowerCase(),
					description: '',
					type: cf.datatype,
					entity: entity
				});
			});

			if (entity === 'order') {
				const orderRowCustomFields = AppService.getCustomFields('orderrow');
				orderRowCustomFields.forEach(function (cf: CustomField) {
					t.push({
						name: cf.name,
						tag: 'Order_Row' + '_Custom_' + cf.id,
						pdfTemplateTag: `custom.Order_Row.${cf.id}`,
						tagId: cf.id,
						nameMapper: 'Order_Row',
						entity: 'order'
					});
				});
			}
		}
	});

	// Add udo fields to tags
	if (mainEntity.toLowerCase().includes('userdefinedobject')) {
		const id = mainEntity.replace('userdefinedobject', '');
		const udoFields = AppService.getCustomFields(('userDefined' + (id || 1)) as CustomFieldKey);
		udoFields.forEach((cf: CustomField) => {
			t.push({
				name: cf.name,
				// Object 1 should not have an id in its entity
				tag: `UserDefinedObject${id}.custom_${cf.id}`,
				pdfTemplateTag: `custom.${mainEntity}.${cf.id}`,
				tagId: cf.id,
				nameMapper: mainEntity,
				description: '',
				type: cf.datatype,
				entity: mainEntity
			});
		});
	}

	return t.reduce<EntityTag[]>((a, b) => a.concat(b), []);
};

/*
	Port of TagsService.getImportTags. Note it does NOT lowercase its argument, unlike getEntityTags —
	that asymmetry is in the Angular original and the only caller (the import mapper) passes lowercase
	names already. The product branch also splices a nested ARRAY in rather than spreading it; the
	trailing flat() undoes that, so it is harmless, but it is kept verbatim rather than tidied.
*/
export const getImportTags = (entity: string): EntityTag[] => {
	const { tags, tagsByEntity } = buildTags();
	let t: (EntityTag | EntityTag[])[] = [];
	const tagGroups = [...tagsByEntity[entity]];
	tagGroups.unshift(entity);

	tagGroups.forEach(function (subTags) {
		const importTags = (tags[subTags] ?? []).filter(tag => tag.import === true);

		if (subTags === 'contact') {
			const salutationTagIndex = importTags.findIndex(tag => tag.tag === 'Contact.Salutation');

			if (salutationTagIndex > -1) {
				const salutationTag = importTags[salutationTagIndex];
				const clone = { ...salutationTag };
				clone.name = T('tag.contact.salutation');
				importTags[salutationTagIndex] = clone;
			}
		}

		t.push(importTags);
	});

	if (entity === 'product') {
		const meta = AppService.getMetadata();
		const currenciesMasterFirst: Currency[] = meta.customerCurrencies
			.filter((currency: Currency) => currency.active)
			.sort((a: Currency, b: Currency) => Number(b.masterCurrency) - Number(a.masterCurrency));
		t = t.flat();

		// Same array as t — flat() above already removed the nesting, so the splices below mutate t.
		const flat = t as EntityTag[];
		const priceIndex = flat.findIndex(importTag => importTag.tag === 'Product.Price');
		const costTag = flat.find(importTag => importTag.tag === 'Product.PurchasePrice') as EntityTag;

		let priceTags = addCurrenciesToTag(currenciesMasterFirst, flat[priceIndex]);
		let costTags = addCurrenciesToTag(currenciesMasterFirst, costTag);
		const costTagsWithAllPriceLists: EntityTag[] = [];

		{
			const priceLists = AppService.getPriceLists().filter(priceList => priceList.active);

			const idString = priceLists.reduce((idString: string, priceList) => (idString += `_${priceList.id}`), '');
			costTags.forEach(costTag => {
				costTagsWithAllPriceLists.push({
					...costTag,
					name: `${costTag.name} - ${T('tag.product.allPriceLists')}`,
					tag: `${costTag.tag}.priceList${idString}.isDefault`,
					tagId: `${costTag.tagId}.priceList${idString}.isDefault`
				});
			});

			priceTags = addPriceListsToTag(priceTags, priceLists);
			costTags = addPriceListsToTag(costTags, priceLists);
		}

		flat.splice(priceIndex, 1, ...priceTags);
		const costIndex = flat.findIndex(importTag => importTag.tag === 'Product.PurchasePrice');
		// Splices in a nested ARRAY rather than spreading it — a real pre-existing bug, kept verbatim.
		// The trailing t.flat() undoes it, which is why nobody has noticed. The cast is what makes the
		// nesting visible to the type checker rather than papering over it.
		flat.splice(costIndex, 1, [...costTagsWithAllPriceLists, ...costTags] as unknown as EntityTag);
	}

	if (nameMapper.hasOwnProperty(entity)) {
		if (entity === 'client') entity = 'account';
		const fields = AppService.getCustomFields(entity as CustomFieldKey);
		fields.forEach(function (cf: CustomField) {
			t.push({
				name: cf.name,
				tag: nameMapper[entity] + '.custom_' + cf.id,
				pdfTemplateTag: `custom.${nameMapper[entity].toLowerCase()}.${cf.id}`,
				tagId: cf.id,
				nameMapper: nameMapper[entity].toLowerCase(),
				description: '',
				importMatch: true,
				datatype: cf.datatype
			});
		});
	}

	return t.flat();

	function addCurrenciesToTag(currencies: Currency[], tagToGetCurrencies: EntityTag): EntityTag[] {
		const currencyTags: EntityTag[] = [];

		currencies.forEach(function (currency) {
			currencyTags.push({
				...tagToGetCurrencies,
				name: `${tagToGetCurrencies.name} (${currency.iso})`,
				tag: `${tagToGetCurrencies.tag}.currency_${currency.iso}`,
				tagId: `${tagToGetCurrencies.tag}.currency_${currency.iso}`,
				nameMapper: nameMapper[entity].toLowerCase()
			});
		});
		return currencyTags;
	}

	function addPriceListsToTag(tagsToGetPriceLists: EntityTag[], priceLists: PriceList[]): EntityTag[] {
		const priceListTags: EntityTag[] = [];
		tagsToGetPriceLists.forEach(tagToGetPriceLists => {
			priceLists.forEach(priceList => {
				let formattedPriceListName = priceList.name;
				if (priceList.code) {
					formattedPriceListName += ` (${priceList.code})`;
				}

				let formattedPriceListTag = `.priceList_${priceList.id}`;
				if (priceList.isDefault) {
					formattedPriceListTag += '.isDefault';
				}

				priceListTags.push({
					...tagToGetPriceLists,
					name: `${tagToGetPriceLists.name} - ${formattedPriceListName}`,
					tag: `${tagToGetPriceLists.tag}${formattedPriceListTag}`,
					tagId: `${tagToGetPriceLists.tagId}${formattedPriceListTag}`
				});
			});
		});
		return priceListTags;
	}
};

/*
	Only for the Angular remainder's getTagsByEntity2, which needs the raw catalogue to build mail
	tags. It exists so there is still exactly ONE catalogue while the mail half is unported, and goes
	away with that half. Do not reach for it from React.
*/
export const getTagCatalogue = () => ({ ...buildTags(), nameMapper, customFieldMapper });
